/**
 * 枠（`FlowWorkspace`）が図の面（`FlowCanvas`）へ配る、縮尺・左右比較・視点の同期。
 *
 * **props ではなく文脈で配る。** 図の面を組み立てるのは画面ごとの部品（業務フロー、
 * 業務ロジックの処理フロー）で、枠と面のあいだに画面の部品が挟まる。縮尺と同期は「どの図でも
 * 同じであるべき設定」なので、挟まる部品に 1 つずつ中継させると、中継を忘れた図だけが
 * 倍率 1 のまま・左右が別々に動く、という食い違いが黙って生まれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 枠が決めた縮尺・左右比較の状態・視点の同期を、図の面へ中継なしで配る。
 * @graph-connects none
 */
import type { Viewport } from '@xyflow/react';
import { createContext, useContext } from 'react';

/**
 * 左右 2 面の視点を揃える口。
 *
 * 片方の面で読み手が動かした視点を、もう片方へ写す。写した側の図も視点が動いたことを知らせて
 * くるので、そのまま送り返すと 2 面が互いに書き合って止まらない。送るのは読み手の操作から来た
 * 動きだけにし（`FlowCanvas` が判定する）、写すのは次の描画の直前に 1 回へまとめる。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右比較の 2 面のあいだで、読み手が動かした視点をもう片方へ写す。
 * @graph-connects none
 */
export interface FlowViewportSync {
  /**
   * 面を登録する。戻り値を呼ぶと登録を外す。
   *
   * @param side 面の名前
   * @param apply その面の視点を書き換える口
   */
  register: (side: string, apply: (viewport: Viewport) => void) => () => void;
  /**
   * ある面で動いた視点を、ほかの面へ写す。
   *
   * @param side 動いた面の名前
   * @param viewport 動いた後の視点
   */
  publish: (side: string, viewport: Viewport) => void;
}

/**
 * 左右 2 面の視点を揃える口を作る。
 *
 * 写すのは `requestAnimationFrame` で次の描画の直前に 1 回だけにする。パンの途中は 1 フレームに
 * 何度も視点が動くので、その都度写すと片方の面だけ描き直しが追いつかずに揺れる。最後の 1 つを
 * 写せば足りる。
 *
 * @returns 視点を揃える口
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右比較の 2 面の視点を、次の描画の直前に 1 回だけ写して揃える口を作る。
 * @graph-connects none
 */
export function createViewportSync(): FlowViewportSync {
  const appliers = new Map<string, (viewport: Viewport) => void>();
  let pending: { side: string; viewport: Viewport } | null = null;
  let scheduled = false;

  const flush = () => {
    scheduled = false;
    const moved = pending;
    pending = null;
    if (moved === null) return;
    for (const [side, apply] of appliers) {
      if (side !== moved.side) apply(moved.viewport);
    }
  };

  return {
    register: (side, apply) => {
      appliers.set(side, apply);
      return () => {
        if (appliers.get(side) === apply) appliers.delete(side);
      };
    },
    publish: (side, viewport) => {
      pending = { side, viewport };
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(flush);
    },
  };
}

/**
 * 図の面が枠から受け取るもの。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の面が枠から受け取る縮尺・左右比較の案件番号・視点の同期を表す。
 * @graph-connects none
 */
export interface FlowPaneContextValue {
  /** 初期表示の縮尺。左右比較では両面で同じ値。 */
  scale: number;
  /**
   * 図の器の幅を枠へ知らせる口。枠の外で描くときは null。
   *
   * 枠が測れるのは図を収める格子の幅で、図の器の幅ではない（1 面のときは器が区画の余白の内側に
   * 入る）。格子の幅で縮尺を決めると、余白のぶん式の値より大きく縮尺が出る。図の面が自分の器を
   * 測って知らせ、枠はその幅で縮尺を決め直す。
   */
  reportPaneWidth: ((width: number) => void) | null;
  /** 縮尺が図の器の実測から決まったか。決まるまで図の面は React Flow を描かない（最初の視点は描いた時点でしか読まれない）。 */
  paneMeasured: boolean;
  /** 左右に並べているときの案件番号（見出しに出す）。1 面のときは null。 */
  comparePj: number | null;
  /** 左右に並べているときの視点の同期。1 面のときは null。 */
  sync: FlowViewportSync | null;
  /**
   * 図の枠を静かにするか。1 面の見出しを出さず、拡大・縮小のボタンを図に乗った時だけ出す。
   * 省けば出す（業務のデータの流れの図）。業務フローの図とステージの図が `true` にする。
   */
  quiet?: boolean;
}

/** 枠の外で図を描いたとき（単体のテストなど）の既定。実寸・1 面・同期なし・測らずに描く。 @graph-connects none */
const DEFAULT_PANE_CONTEXT: FlowPaneContextValue = {
  scale: 1,
  reportPaneWidth: null,
  paneMeasured: true,
  comparePj: null,
  sync: null,
};

/** 枠が図の面へ配る文脈。 @graph-connects none */
export const FlowPaneContext = createContext<FlowPaneContextValue>(DEFAULT_PANE_CONTEXT);

/**
 * 枠が配った縮尺・左右比較・視点の同期を読む。
 *
 * @returns 図の面が枠から受け取るもの
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の面が、枠の決めた縮尺・左右比較・視点の同期を読む。
 * @graph-connects none
 */
export function useFlowPaneContext(): FlowPaneContextValue {
  return useContext(FlowPaneContext);
}
